import { el, btn, backButton } from '@/ui/dom';
import { XP_STAR_SVG, SP_COIN_SVG } from '@/ui/currencyIcons';
import { span } from '@/dev/perf';
import { VEHICLE_ORDER } from '@/entities/vehicles';
import { tuning } from '@/config/tuning';
import { unlockThresholdFor } from '@/config/racers';
import { isVehicleUnlocked, nextUnlock } from '@/core/Progression';
import {
  equipCosmetic,
  equippedPaintJob,
  unequipPaintJob,
  loadoutFor,
  equipMod,
  equippedMods,
  clearMods,
  kitNumberFor,
  setKitNumber,
  tifoStyleFor,
  setTifoStyle,
  spBalance,
} from '@/core/skillPoints';
import { catTab } from '@/ui/catTabs';
import { renderNumberChips, renderStyleChips } from '@/ui/modPickers';
import { resolveLook } from '@/entities/carLook';
import {
  cosmeticById,
  cosmeticsFor,
  MOD_KITNUMBER_ID,
  MOD_TIFO_ID,
  RARITY_META,
  type CosmeticItem,
} from '@/config/cosmetics';
import { cosmeticCardArt, cosmeticTypeTag, paintJobItems } from '@/ui/cosmeticArt';
import { GarageStage, type BayTarget } from '@/ui/garage/GarageStage';
import { fauxCentreX, portraitCarAnchor } from '@/ui/garage/dockLayout';
import { ACCENTS, BLURBS, STAT_BARS, vehicleLabel } from '@/ui/vehicleCatalog';
import { sfxUiTick } from '@/audio/sfx';
import type { SaveData } from '@/core/SaveData';
import { subscribeViewport } from '@/ui/viewport';
import { lockCta, LOCK_SVG, BUY_GAME_LABEL } from '@/ui/lockCta';
import { promoPanel, type PromoPanel } from '@/ui/promoPanel';

/**
 * GarageScreen.ts — the GARAGE as a full-bleed showroom, redesigned M113 to
 * the owner's mockup over the new bg art (public/garage/bg.webp — a black
 * brick garage with a PAINTED gold turntable front-centre and painted empty
 * parking bays behind). Composition: the hero ride rotates ON the painted
 * turntable, the other rides park IN the painted bays, big SHOP + CUSTOMIZE
 * action buttons stack down the left edge, flanking chevrons cycle rides, and
 * one wide glass bar along the bottom carries name + chips + full-word stat
 * rows + the gold context CTA. Same three contexts as ever (title garage /
 * quick-race confirm / tour start) via the unchanged GarageOptions API.
 */

export interface GarageOptions {
  /** Small label under the title (e.g. "WORLD TOUR" / "QUICK RACE" / "GARAGE"). */
  kicker: string;
  /** Primary button label (e.g. "START TOUR" / "START RACE" / "SAVE RIDE"). */
  confirmLabel: string;
  onConfirm: (vehicleId: string) => void;
  onBack: () => void;
  /** M79 — opens THE SHOP (present only on the title-menu garage). */
  /** M91: receives the garage's BROWSED vehicle so the shop dresses the car
   *  you're actually looking at (it used to silently target the last-SAVED
   *  ride — buying "for" a browsed car equipped the wrong one). */
  onShop?: (vehicleId: string) => void;
  /** M87 — persist a CUSTOMIZE equip (present only with onShop; the overlay
   *  needs somewhere to save, and it rides the same title-garage entry). */
  onSaveChange?: (save: SaveData) => void;
  /** Owner round (2026-10-02): the mode this garage fronts is part of the
   *  paid game and the player does not own it. The garage still opens (the
   *  player can browse their ride) but carries the lock banner, and the
   *  confirm button becomes BUY THE GAME (→ onBuy) on every ride. */
  lock?: {
    line: string;
    price: string;
    onBuy: () => void;
    /** M138: the small "ALREADY OWN IT? LOG IN" link (signed-out only). */
    onLogIn?: () => void;
    /** false = keep the normal confirm (the title GARAGE's SAVE RIDE): the
     *  banner sells, the button still saves the starter ride. Default true
     *  (the tour garage's START TOUR becomes BUY THE GAME). */
    confirmBuys?: boolean;
  };
  /** M138 (owner): a SIGNED-OUT visitor — XP and SP belong to a Racer ID,
   *  so the wallet is hidden (the save is already a fresh guest's). */
  guest?: boolean;
  /** M139 (owner: "the buy module needs to be the main visual grab; the
   *  rides behind should appear in sequence to promote each"): a SIGNED-OUT
   *  web visitor's showroom. Needs `lock` (price / buy / log in). Every ride
   *  is shown lit (not as a dark ghost) and the turntable cycles through all
   *  of them; the big promo panel replaces the small banner; the confirm is
   *  BUY THE GAME; CUSTOMIZE opens the customize promo (`onCustomize`). */
  promo?: {
    kicker: string;
    headline: string;
    sub?: string;
    onCustomize?: (vehicleId: string) => void;
  };
}

/** M139 — seconds each ride holds the turntable in the promo showroom. */
const PROMO_RIDE_MS = 3400;

const PADLOCK =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 1 1 6 0v3H9Z"/></svg>';

/** Icon tiles for the big left action buttons (mockup language). */
const CART_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 4h2.4l2 11.2a1.6 1.6 0 0 0 1.6 1.3h7.9a1.6 1.6 0 0 0 1.6-1.2L20.6 8H6.1"/><circle cx="9.6" cy="20" r="1.5"/><circle cx="16.6" cy="20" r="1.5"/></svg>';
const WRENCH_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.2 6.3a4.4 4.4 0 0 1 5.6-5L16.6 4.5l.6 2.3 2.3.6 3.2-3.2a4.4 4.4 0 0 1-5 5.6L8.8 18.7a2.1 2.1 0 0 1-3-3l8.4-9.4Z"/><path d="m5 13.5-2.7 2.6a2.5 2.5 0 0 0 0 3.6l2 2a2.5 2.5 0 0 0 3.6 0l2.6-2.7"/></svg>';

/* ---- M113 stage anchors — matched to the painted composition of
 * public/garage/bg.webp (1536x1024: turntable ellipse centre ~x 47.5% /
 * y 75%; the empty bays band y ~44-53% with slots near x 20 / 33 / 47 /
 * 61 / 80%). Tall-landscape anchors are IMAGE-space fractions pushed
 * through the CSS cover transform per real viewport (fixed screen
 * fractions drift off the painted turntable as the aspect changes);
 * short-landscape and portrait keep hand placements because their crops
 * hide most of the painted floor. */
const BG_IMG = { w: 1536, h: 1024 };
const TURNTABLE_IMG = { x: 0.475, y: 0.75 };
const HERO_Y_MAX = 0.8; // very wide crops would push the car into the bar
const HERO_ANCHOR_SHORT = { x: 0.47, y: 0.65 }; // 375px-high landscape phones
const HERO_ANCHOR_PORTRAIT = { x: 0.5, y: 0.6 }; // wheels kissing the ring's top arc
/** The painted bay slots (image-space x, one per non-hero ride). M114: a
 *  sixth slot for the F1-era 7-ride roster — the first five track the painted
 *  bays, the sixth parks in the open floor right of them. */
const BAY_XS = [0.2, 0.335, 0.47, 0.61, 0.735, 0.865];
/** M114 (owner: "parked vehicles are floating"): the anchor is the car's
 *  GROUND-CONTACT point, so it must sit on the painted bays' FLOOR (the
 *  yellow boxes span image y ~0.48-0.53) — the old 0.475 was the wall base,
 *  which read as cars hovering above their bays. */
const BAY_IMG_Y = 0.515;
const BAY_Y_SHORT = 0.5;
/** Short landscape uses RAW screen fractions (the crop hides the painted
 *  bays) — its own row keeps the sixth ride clear of the right chevron. */
const BAY_XS_SHORT = [0.17, 0.3, 0.43, 0.56, 0.68, 0.8];

/** Map an image-space fraction to a screen fraction under cover-cropping. */
function coverFrac(fx: number, fy: number, w: number, h: number): { x: number; y: number } {
  const s = Math.max(w / BG_IMG.w, h / BG_IMG.h);
  const dw = BG_IMG.w * s;
  const dh = BG_IMG.h * s;
  return { x: (fx * dw - (dw - w) / 2) / w, y: (fy * dh - (dh - h) / 2) / h };
}
/** Portrait crops the painted bays away — park the lineup at the wall base
 *  (higher bands read as cars mounted ON the wall, measured at 450x775). */
const BAY_PORTRAIT = { x0: 0.16, x1: 0.84, y: 0.455 };
/* ---- M115 CUSTOMIZE anchors. The overlay now wears the SHOP's composition
 * (owner: "similar to the existing module format and sizing on the Shop"), so
 * the car is framed with the shop's own PAINT JOBS numbers: prominent in the
 * clear zone left of the docked panel on desktop/tall landscape (at the
 * derived faux-centre), and big above the bottom sheet on phones. Kept as
 * their own constants rather than imported from ShopScreen so a future shop
 * tweak can't silently move the garage. */
const CUST_LAND = { x: 0, y: 0.68, zoom: 1.12 }; // x comes from fauxCentreX()
const CUST_SHORT = { x: 0.3, y: 0.6, zoom: 2.0 }; // ≤500px-high landscape
/* Portrait's real numbers come from dockLayout.ts portraitCarAnchor (M132 —
 * derived from the live dock edge); this entry only keeps `c` truthy there. */
const CUST_PORTRAIT = { x: 0.5, y: 0.47, zoom: 2.5 };
/** Off the painted turntable while customizing — the shop's grounding ring. */
const CUST_RING_SCALE = 0.3;
/** The painted gold turntable IS the pedestal now — the stage's own drawn
 *  rings dim to a whisper (contact shadows stay full). */
const HERO_RING_SCALE = 0.12;

/** Vehicle CLASS chip copy (gold outline chip beside the name — the mockup's
 *  type badge). Truthful to the tuning ladder; no vehicle rarity exists. */
const VEHICLE_TYPES: Record<string, string> = {
  coupe: 'STARTER',
  pickup: 'HEAVY DUTY',
  muscle: 'POWERHOUSE',
  sports: 'PRECISION',
  van: 'HEAVYWEIGHT',
  motorcycle: 'FEATHERWEIGHT',
  f1: 'ULTIMATE',
};

/** The CUSTOMIZE overlay's two category filters — 'paintjobs' is the M87
 *  MERGED body slot (a kit and a flag wrap can never both be worn). */
type CustTab = 'paintjobs' | 'tires' | 'mods';
const CUST_TABS: { key: CustTab; label: string }[] = [
  { key: 'paintjobs', label: 'PAINT JOBS' },
  { key: 'tires', label: 'TIRES & RIMS' },
  { key: 'mods', label: 'MODS' },
];

export class GarageScreen {
  private readonly root: HTMLDivElement;
  private readonly stageHost: HTMLDivElement;
  private stage: GarageStage | null = null;
  private readonly infoEl: HTMLDivElement;
  private readonly pointsValueEl: HTMLDivElement;
  private readonly pointsNextEl: HTMLDivElement;
  private readonly pointsBarEl: HTMLSpanElement;
  private readonly spValueEl: HTMLDivElement;
  private readonly statsPanel: HTMLDivElement;
  private readonly statsRows: { fill: HTMLSpanElement }[] = [];
  private readonly vehNameEl: HTMLDivElement;
  private readonly typeChipEl: HTMLSpanElement;
  private readonly lockChipEl: HTMLSpanElement;
  private readonly dotsEl: HTMLDivElement;
  private readonly blurbEl: HTMLDivElement;
  private readonly prevBtn: HTMLButtonElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly confirmBtn: HTMLButtonElement;
  private readonly confirmLabelEl: HTMLSpanElement;
  /** The lock banner while the fronted mode is not owned (opts.lock). */
  private lockEl: HTMLDivElement | null = null;
  private walletEl!: HTMLDivElement;
  /** M139 — the promo showroom (signed-out web): panel + autoplay timer. */
  private promo: PromoPanel | null = null;
  private promoTimer: number | null = null;
  private shopBtn!: HTMLButtonElement;

  private custBtn!: HTMLButtonElement;
  private readonly custPanel: HTMLDivElement;
  private readonly custFiltersEl: HTMLDivElement;
  private readonly custGridEl: HTMLDivElement;
  private readonly custRideEl: HTMLDivElement;
  private readonly custDock: HTMLDivElement;
  private readonly custDetailName: HTMLDivElement;
  private readonly custDetailType: HTMLSpanElement;
  private readonly custDetailRarity: HTMLSpanElement;
  private readonly custDetailDesc: HTMLDivElement;
  private readonly custPickEl: HTMLDivElement;
  private readonly custActionBtn: HTMLButtonElement;

  private opts: GarageOptions | null = null;
  private save: SaveData | null = null;
  private unlockedIds: string[] = [];
  private selected = 'coupe';
  /** M87 — the CUSTOMIZE overlay is up (hero parks aside, panel over the rest). */
  private customizing = false;
  /** M115 — which category the overlay's filter chips are showing. */
  private custTab: CustTab = 'paintjobs';
  /** M115 — the BROWSED item in the overlay (null = the STOCK card). It only
   *  previews on the car; EQUIP commits it (the shop's proven two-step). */
  private custSel: string | null = null;
  private readonly onWindowResize = (): void => {
    if (this.root.style.display === 'none') return;
    this.stage?.resize();
    this.layoutStage();
  };
  /** M113 — keyboard cycling (←/→) + Esc closes the customize overlay. */
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (this.root.style.display === 'none') return;
    if (e.key === 'Escape') {
      if (this.customizing) {
        e.preventDefault();
        this.exitCustomize();
      }
      return;
    }
    if (this.customizing) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      sfxUiTick();
      this.cycle(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      sfxUiTick();
      this.cycle(1);
    }
  };

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen screen-garage', parent);
    this.root.style.display = 'none';

    // Ambient life over the painted backdrop (CSS-only, parked under reduced
    // motion): hex ceiling-ring glow, a slow floor light sweep, a pulse on the
    // painted turntable, drifting dust motes. Sits UNDER the 3D canvas.
    const amb = el('div', 'garage-amb', this.root);
    el('div', 'garage-amb-hex', amb);
    el('div', 'garage-amb-sweep', amb);
    el('div', 'garage-amb-ring', amb);
    for (let i = 0; i < 5; i++) el('div', `garage-amb-mote garage-amb-mote-${i + 1}`, amb);

    // Full-bleed 3D stage over the garage backdrop (root's CSS background).
    this.stageHost = el('div', 'garage-stage', this.root);

    // Top strip: back + title on the left, the CAREER · BANK chip on the right.
    const top = el('div', 'garage-top', this.root);
    const titles = el('div', 'garage-titles', top);
    const titleRow = el('div', 'garage-titlerow', titles);
    // M114b: the ONE shared back chevron. It sits INLINE here because the
    // garage already lays out a top-left title row for it (corner: false).
    // Owner 2026-09-16: while the CUSTOMIZE overlay is up, back (the chevron
    // AND the edge swipe, which registers this same action) closes the
    // overlay back to the garage main — it used to leave for the title.
    backButton(
      titleRow,
      () => {
        if (this.customizing) this.exitCustomize();
        else this.opts?.onBack();
      },
      { corner: false },
    );
    const title = el('div', 'garage-title', titleRow);
    title.textContent = 'CHOOSE YOUR RIDE';
    title.dataset.text = 'CHOOSE YOUR RIDE'; // chrome face + extruded edge (CSS)
    this.infoEl = el('div', 'garage-info', titles);

    const right = el('div', 'garage-topright', top);
    // M132 (owner): the garage shows BOTH currencies as a wallet — the gold XP
    // module drawn as PROGRESS (total, a short "N XP TO <RIDE>" note and a
    // bar to the next unlock, the M131 rule) beside the green SP module drawn
    // as a BALANCE (what the shop spends). Portrait lays the wallet out as a
    // full-width strip under the title (CSS); landscape keeps it top-right.
    const wallet = el('div', 'garage-wallet', right);
    this.walletEl = wallet;
    const points = el('div', 'garage-points garage-xp', wallet);
    const lbl = el('div', 'garage-points-label', points);
    const ic = el('span', 'garage-points-ic', lbl);
    ic.innerHTML = XP_STAR_SVG;
    lbl.append('XP');
    this.pointsValueEl = el('div', 'garage-points-value', points);
    this.pointsNextEl = el('div', 'garage-points-next', points);
    const track = el('span', 'garage-points-track', points);
    this.pointsBarEl = el('span', 'garage-points-fill', track);
    const sp = el('div', 'garage-points garage-sp', wallet);
    const spLbl = el('div', 'garage-points-label', sp);
    const spIc = el('span', 'garage-points-ic', spLbl);
    spIc.innerHTML = SP_COIN_SVG;
    spLbl.append('SP');
    this.spValueEl = el('div', 'garage-points-value', sp);
    el('div', 'garage-points-next', sp).textContent = 'SHOP POINTS';

    // M113 — the big left action stack (mockup): SHOP + CUSTOMIZE as icon-tile
    // buttons. Same callbacks + visibility rules as the old top-right buttons.
    const side = el('div', 'garage-side', this.root);
    this.shopBtn = this.buildAction(
      side,
      'garage-act-shop',
      'SHOP',
      'PAINT JOBS · WHEELS',
      CART_ICON,
      () => this.opts?.onShop?.(this.selected),
    );
    this.custBtn = this.buildAction(
      side,
      'garage-act-cust',
      'CUSTOMIZE',
      'UPGRADE & STYLE',
      WRENCH_ICON,
      () =>
        this.opts?.promo ? this.opts.promo.onCustomize?.(this.selected) : this.enterCustomize(),
    );

    // M113 — big flanking chevrons cycle the lineup (the old picker arrows).
    this.prevBtn = btn('‹', 'btn garage-chev garage-chev-l', this.root, () => {
      this.cycle(-1);
      this.restartPromo();
    });
    this.nextBtn = btn('›', 'btn garage-chev garage-chev-r', this.root, () => {
      this.cycle(1);
      this.restartPromo();
    });

    // Bottom: ONE wide glass bar — name + chips | full-word stats | gold CTA.
    const bottom = el('div', 'garage-bottom', this.root);

    const nameCol = el('div', 'garage-namecol', bottom);
    el('div', 'garage-name-label', nameCol).textContent = 'YOUR RIDE';
    const nameRow = el('div', 'garage-namerow', nameCol);
    this.vehNameEl = el('div', 'garage-veh-name', nameRow);
    this.typeChipEl = el('span', 'garage-typechip', nameRow);
    this.lockChipEl = el('span', 'garage-lockchip', nameRow);
    this.dotsEl = el('div', 'garage-dots', nameCol);
    this.blurbEl = el('div', 'garage-blurb', nameCol);

    this.statsPanel = el('div', 'garage-statspanel', bottom);
    el('div', 'garage-stats-title', this.statsPanel).textContent = 'VEHICLE STATS';
    const labels = ['SPEED', 'ACCELERATION', 'HANDLING', 'WEIGHT'];
    STAT_BARS.forEach((s, i) => {
      const row = el('div', 'garage-stat', this.statsPanel);
      el('span', 'garage-stat-label', row).textContent = labels[i] ?? s.label;
      const track = el('span', 'garage-stat-track', row);
      const fill = el('span', 'garage-stat-fill', track);
      this.statsRows.push({ fill });
    });

    const actions = el('div', 'garage-actions', bottom);
    this.confirmBtn = btn('', 'btn garage-go', actions, () => {
      if (this.opts?.lock && (this.opts.promo || this.opts.lock.confirmBuys !== false)) {
        this.opts.lock.onBuy();
        return;
      }
      // Browsing a locked ride disables this button; stay defensive anyway.
      if (!this.unlockedIds.includes(this.selected)) return;
      this.opts?.onConfirm(this.selected);
    });
    this.confirmLabelEl = el('span', 'garage-go-label', this.confirmBtn);
    el('span', 'garage-go-chev', this.confirmBtn).textContent = '›';

    // M87/M115: the CUSTOMIZE overlay. Rebuilt M115 in the SHOP's language
    // (owner: the old panel "takes up too much of the screen compared to the
    // car") — a glass column docked down the RIGHT at the shop's exact width
    // (a bottom sheet in portrait), carrying category FILTER chips + one
    // scrolling grid of the items you own, while the car owns the clear zone
    // on the left exactly as it does in the shop.
    this.custPanel = el('div', 'garage-cust', this.root);
    this.custPanel.style.display = 'none';
    const custHead = el('div', 'garage-cust-head', this.custPanel);
    const custTitles = el('div', 'garage-cust-titles', custHead);
    el('div', 'garage-cust-kicker', custTitles).textContent = 'CUSTOMIZE';
    this.custRideEl = el('div', 'garage-cust-ride', custTitles);
    btn('✕', 'btn btn-ghost garage-cust-close', custHead, () => this.exitCustomize());
    // Category tabs in the home-menu language replace the old chips (M128c).
    this.custFiltersEl = el('div', 'cat-tabs garage-cust-tabs', this.custPanel);
    this.custGridEl = el('div', 'garage-cust-grid', this.custPanel);
    const custFoot = el('div', 'garage-cust-foot', this.custPanel);
    const moreBtn = btn('', 'btn garage-cust-shop', custFoot, () =>
      this.opts?.onShop?.(this.selected),
    );
    moreBtn.innerHTML =
      '<span>GET MORE AT THE SHOP</span><span class="garage-shopbtn-chev">›</span>';
    btn('DONE', 'btn btn-primary garage-cust-done', custFoot, () => this.exitCustomize());

    // The overlay's detail dock — the SHOP's own element + classes, so it
    // inherits that geometry (and its portrait/short-landscape rules) exactly.
    this.custDock = el('div', 'shop-detaildock garage-custdock', this.root);
    this.custDock.style.display = 'none';
    const cdHead = el('div', 'shop-detail-head', this.custDock);
    this.custDetailName = el('div', 'shop-detail-name', cdHead);
    this.custDetailType = el('span', 'shop-type', cdHead);
    this.custDetailRarity = el('span', 'shop-rarity', cdHead);
    this.custDetailDesc = el('div', 'shop-detail-desc', this.custDock);
    this.custPickEl = el('div', 'shop-numpick', this.custDock); // M128c: number / cape-design chips
    this.custPickEl.style.display = 'none';
    this.custActionBtn = btn('', 'btn shop-action', this.custDock, () => this.custEquip());

    subscribeViewport(this.onWindowResize); // app-lifetime screen — never unsubscribed
    window.addEventListener('keydown', this.onKeyDown); // app-lifetime screen
  }

  /** One big left-edge action button: gradient icon tile + two-line text. */
  private buildAction(
    parent: HTMLElement,
    cls: string,
    title: string,
    sub: string,
    icon: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const b = btn('', `btn garage-act ${cls}`, parent, onClick);
    const ico = el('span', 'garage-act-ico', b);
    ico.innerHTML = icon; // static SVG constant
    const txt = el('span', 'garage-act-txt', b);
    el('span', 'garage-act-title', txt).textContent = title;
    el('span', 'garage-act-sub', txt).textContent = sub;
    return b;
  }

  /** Loading round: create the stage + warm its context/models at idle so
   *  the first GARAGE tap is instant (see GarageStage.prewarm). */
  prewarm(): void {
    if (!this.stage) this.stage = span('garage.stage', () => new GarageStage(this.stageHost));
    void this.stage.prewarm();
  }

  show(save: SaveData, opts: GarageOptions): void {
    this.opts = opts;
    this.save = save;
    this.infoEl.textContent = opts.kicker;
    this.confirmLabelEl.textContent = opts.confirmLabel;
    this.lockEl?.remove();
    this.lockEl = null;
    this.promo?.root.remove();
    this.promo = null;
    this.stopPromo();
    const promoMode = !!(opts.promo && opts.lock);
    this.root.classList.toggle('garage-promo', promoMode);
    if (opts.promo && opts.lock) {
      this.promo = promoPanel(this.root, {
        ...opts.promo,
        price: opts.lock.price,
        onBuy: opts.lock.onBuy,
        onLogIn: opts.lock.onLogIn,
        className: 'garage-promopanel',
      });
    } else if (opts.lock) {
      this.lockEl = lockCta(this.root, { ...opts.lock, className: 'garage-lockcta' });
    }
    this.walletEl.style.display = opts.guest ? 'none' : '';
    this.unlockedIds = VEHICLE_ORDER.map((v) => v.id).filter((id) => isVehicleUnlocked(save, id));
    this.refreshPoints();
    // The game's reduced-motion SETTING parks the ambient layers (the OS media
    // query covers the rest — the M78 pattern).
    this.root.classList.toggle('reduced', save.settings.reducedMotion);

    if (!this.stage) this.stage = span('garage.stage', () => new GarageStage(this.stageHost));
    this.stage.onPick = (id): void => {
      sfxUiTick();
      this.select(id);
      this.restartPromo();
    };
    // The painted turntable is the pedestal now — drawn rings go faint.
    this.stage.setRingScale(HERO_RING_SCALE);
    this.shopBtn.style.display = opts.onShop ? '' : 'none';
    this.custBtn.style.display = opts.onShop && opts.onSaveChange ? '' : 'none';
    this.customizing = false;
    this.custPanel.style.display = 'none';
    this.custDock.style.display = 'none';
    this.root.classList.remove('customizing');
    // M79: every unlocked ride wears its equipped shop look in the showroom.
    this.stage.setVehicles(
      VEHICLE_ORDER.map((v) => ({
        id: v.id,
        // M139: the promo showroom shows every ride in its real livery — a
        // dark ghost sells nothing.
        unlocked: promoMode || this.unlockedIds.includes(v.id),
        look: resolveLook(loadoutFor(save, v.id)),
      })),
    );
    this.buildDots();

    // Coerce the initial pick to an unlocked ride (parseSave already keeps the
    // saved playerVehicle unlocked, but stay defensive at the UI boundary too).
    const saved = save.settings.playerVehicle || 'coupe';
    this.selected = ''; // force a full select pass
    this.select(this.unlockedIds.includes(saved) ? saved : 'coupe');

    this.stage.show();
    this.root.style.display = '';
    requestAnimationFrame(() => {
      this.stage?.resize();
      this.layoutStage();
    });
    if (promoMode) this.restartPromo();
  }

  hide(): void {
    this.stopPromo();
    this.stage?.hide();
    this.root.style.display = 'none';
  }

  /** M139 — the showroom autoplay: every PROMO_RIDE_MS the next ride drives
   *  onto the turntable. Any manual browse restarts the clock (the player's
   *  pick gets its full beat). Reduced motion: no autoplay (the chevrons and
   *  dots still browse every ride). */
  private restartPromo(): void {
    this.stopPromo();
    if (!this.opts?.promo || this.save?.settings.reducedMotion) return;
    this.promoTimer = window.setInterval(() => this.cycle(1), PROMO_RIDE_MS);
  }

  private stopPromo(): void {
    if (this.promoTimer !== null) {
      window.clearInterval(this.promoTimer);
      this.promoTimer = null;
    }
  }

  // ----- customize (M87) -----------------------------------------------------

  private enterCustomize(): void {
    if (!this.save || this.customizing) return;
    sfxUiTick();
    this.customizing = true;
    this.custTab = 'paintjobs';
    this.custSel = this.custEquipped(); // open on what the car is wearing
    this.custRideEl.textContent = vehicleLabel(this.selected).toUpperCase();
    this.renderCustomize();
    this.custPanel.style.display = '';
    this.custDock.style.display = '';
    this.custGridEl.scrollTop = 0;
    this.root.classList.add('customizing'); // CSS parks the bar + side + chevrons
    this.layoutStage(); // the hero glides aside, still front-of-stage
  }

  private exitCustomize(): void {
    if (!this.customizing) return;
    sfxUiTick();
    this.customizing = false;
    this.custPanel.style.display = 'none';
    this.custDock.style.display = 'none';
    this.root.classList.remove('customizing');
    // Drop any uncommitted PREVIEW — the car goes back to what it truly wears.
    if (this.save) {
      this.stage?.setLook(this.selected, resolveLook(loadoutFor(this.save, this.selected)));
    }
    this.layoutStage();
  }

  /** What the overlay's CURRENT category is actually wearing ('paintjobs' is
   *  the M87 merged body slot — kit OR flag, whichever is on). */
  private custEquipped(): string | null {
    if (!this.save) return null;
    if (this.custTab === 'mods') return null; // M128: mods stack (per-card state)
    return this.custTab === 'paintjobs'
      ? equippedPaintJob(this.save, this.selected)
      : (loadoutFor(this.save, this.selected).tires ?? null);
  }

  /** The items the player OWNS in a category (the overlay only ever shows
   *  what's already theirs — the shop sells; this dresses). */
  private custItems(tab: CustTab): CosmeticItem[] {
    const save = this.save;
    if (!save) return [];
    const all =
      tab === 'paintjobs'
        ? paintJobItems('all')
        : tab === 'tires'
          ? cosmeticsFor('tires')
          : [...cosmeticsFor('mod')].sort((p, q) => p.price - q.price); // M128c: cheapest first (owner)
    return all.filter((i) => save.cosmetics.owned.includes(i.id));
  }

  /** The loadout to PREVIEW on the car: what's equipped with the browsed item
   *  overlaid in its slot (or the slot stripped for STOCK) — the shop's rule,
   *  including the M87 merged body slot (previewing a kit clears the wrap). */
  private custPreviewLook(): ReturnType<typeof resolveLook> {
    if (!this.save) return undefined;
    const loadout = { ...loadoutFor(this.save, this.selected) };
    if (this.custTab === 'paintjobs') {
      delete loadout.paint;
      delete loadout.wrap;
      const item = this.custSel ? cosmeticById(this.custSel) : null;
      if (item && (item.category === 'paint' || item.category === 'wrap')) {
        loadout[item.category] = item.id;
      }
    } else if (this.custTab === 'mods') {
      const mods = equippedMods(this.save, this.selected);
      if (this.custSel && !mods.includes(this.custSel)) mods.push(this.custSel);
      if (mods.length > 0) loadout.mods = mods;
      else delete loadout.mods;
    } else if (this.custSel === null) {
      delete loadout.tires;
    } else {
      loadout.tires = this.custSel;
    }
    return resolveLook(loadout);
  }

  private custRefreshPreview(): void {
    this.stage?.setLook(this.selected, this.custPreviewLook());
  }

  /** The overlay's two faces: the category chips (with an owned count each) +
   *  the grid of owned cards, then the detail dock beside the car. */
  private renderCustomize(): void {
    if (!this.save) return;
    this.custFiltersEl.replaceChildren();
    for (const t of CUST_TABS) {
      const n = this.custItems(t.key).length;
      catTab(this.custFiltersEl, {
        key: t.key,
        title: t.label,
        sub: `${n} OWNED`,
        on: this.custTab === t.key,
        onClick: () => {
          if (this.custTab === t.key) return;
          this.custTab = t.key;
          this.custSel = this.custEquipped();
          this.renderCustomize();
          this.custGridEl.scrollTop = 0; // a fresh category starts at the top
          this.custRefreshPreview();
        },
      });
    }

    const equipped = this.custEquipped();
    const on = this.custTab === 'mods' ? equippedMods(this.save, this.selected) : null; // M128
    this.custGridEl.replaceChildren();
    this.custCard(null, on ? on.length === 0 : equipped === null);
    const items = this.custItems(this.custTab);
    for (const item of items) this.custCard(item, on ? on.includes(item.id) : equipped === item.id);
    if (items.length === 0) {
      el('div', 'garage-cust-empty', this.custGridEl).textContent =
        'Nothing owned here yet. THE SHOP has the goods.';
    }
    this.renderCustDetail();
  }

  /** One owned-item card (STOCK leads every slot — it IS the unequip). */
  private custCard(item: CosmeticItem | null, isEquipped: boolean): void {
    const id = item?.id ?? null;
    const selected = this.custSel === id;
    const rarity = item ? RARITY_META[item.rarity] : null;
    const card = btn('', `shop-card owned${selected ? ' selected' : ''}`, this.custGridEl, () => {
      if (this.custSel === id) return;
      sfxUiTick();
      this.custSel = id;
      this.renderCustomize();
      this.custRefreshPreview(); // browse = live preview, EQUIP commits
    });
    if (rarity) card.style.setProperty('--rarity', rarity.color);

    const art = el('div', 'shop-card-art', card);
    if (!item) {
      art.classList.add('shop-card-stock');
      art.textContent = '—';
    } else {
      cosmeticCardArt(item, art);
    }
    el('div', 'shop-card-name', card).textContent = item ? item.name.toUpperCase() : 'STOCK';
    const foot = el('div', 'shop-card-foot', card);
    foot.textContent = isEquipped ? 'EQUIPPED' : item ? cosmeticTypeTag(item) : 'FREE';
    foot.classList.toggle('equipped', isEquipped);
    foot.classList.toggle('owned', !isEquipped);
  }

  /** The dock beside the car: what you're browsing + the green EQUIP action
   *  (M92 rule — equipping is affirmative, so it is GREEN, never red). */
  private renderCustDetail(): void {
    const item = this.custSel ? cosmeticById(this.custSel) : null;
    const equipped = this.custEquipped();
    this.custPickEl.style.display = 'none';
    this.renderCustPicker(item ?? null);
    const modsOn =
      this.custTab === 'mods' && this.save ? equippedMods(this.save, this.selected) : null; // M128
    const isOn = modsOn
      ? item
        ? modsOn.includes(item.id)
        : modsOn.length === 0
      : equipped === (item?.id ?? null);

    if (!item) {
      this.custDetailName.textContent = 'STOCK';
      this.custDetailType.style.display = 'none';
      this.custDetailRarity.style.display = 'none';
      this.custDetailDesc.textContent = 'The factory look. Always yours.';
    } else {
      const rarity = RARITY_META[item.rarity];
      this.custDetailName.textContent = item.name.toUpperCase();
      this.custDetailType.style.display = '';
      this.custDetailType.textContent = cosmeticTypeTag(item); // KIT / FLAG / BOOTS
      this.custDetailRarity.style.display = '';
      this.custDetailRarity.textContent = rarity.label;
      this.custDetailRarity.style.setProperty('--rarity', rarity.color);
      this.custDetailDesc.textContent = item.desc;
    }
    if (modsOn) {
      // M128: a worn mod's button UNEQUIPS it; STOCK removes them all.
      this.custActionBtn.textContent = item
        ? isOn
          ? 'UNEQUIP'
          : 'EQUIP'
        : isOn
          ? 'EQUIPPED'
          : 'REMOVE ALL MODS';
      this.custActionBtn.disabled = !item && isOn;
      this.custActionBtn.classList.toggle('shop-action-equip', !!item && !isOn);
      return;
    }
    this.custActionBtn.textContent = isOn ? 'EQUIPPED' : 'EQUIP';
    this.custActionBtn.disabled = isOn;
    this.custActionBtn.classList.toggle('shop-action-equip', !isOn);
  }

  /** M128c — the two mods that carry a choice get their chips here too (the
   *  overlay only lists OWNED items, so a pick persists on the spot). */
  private renderCustPicker(item: CosmeticItem | null): void {
    if (!this.save || !item) return;
    const veh = this.selected;
    if (item.id === MOD_KITNUMBER_ID) {
      renderNumberChips(this.custPickEl, kitNumberFor(this.save, veh), (n) => {
        if (!this.save) return;
        this.save = setKitNumber(this.save, veh, n);
        this.opts?.onSaveChange?.(this.save);
        this.renderCustDetail();
        this.custRefreshPreview();
      });
    } else if (item.id === MOD_TIFO_ID) {
      renderStyleChips(this.custPickEl, tifoStyleFor(this.save, veh), (n) => {
        if (!this.save) return;
        this.save = setTifoStyle(this.save, veh, n);
        this.opts?.onSaveChange?.(this.save);
        this.renderCustDetail();
        this.custRefreshPreview();
      });
    }
  }

  /** Equip/unequip through the ONE pure path, persist via Flow, re-skin the
   *  hero in place — the whole point: dress the car without the shop. */
  private custEquip(): void {
    if (!this.save || !this.customizing) return;
    sfxUiTick();
    const itemId = this.custSel;
    if (this.custTab === 'mods') {
      // M128: mods stack — STOCK clears them all, a card toggles its own.
      this.save =
        itemId === null
          ? clearMods(this.save, this.selected)
          : equipMod(
              this.save,
              this.selected,
              itemId,
              !equippedMods(this.save, this.selected).includes(itemId),
            );
    } else if (itemId === null) {
      // STOCK clears the whole slot — for PAINT JOBS that's BOTH body
      // categories (the merged slot, M87).
      this.save =
        this.custTab === 'paintjobs'
          ? unequipPaintJob(this.save, this.selected)
          : equipCosmetic(this.save, this.selected, 'tires', null);
    } else {
      const item = cosmeticById(itemId);
      if (!item) return;
      // The item's own catalog category — equipCosmetic swaps the sibling body
      // slot out automatically (kit ↔ flag exclusivity).
      this.save = equipCosmetic(this.save, this.selected, item.category, itemId);
    }
    this.opts?.onSaveChange?.(this.save);
    this.stage?.setLook(this.selected, resolveLook(loadoutFor(this.save, this.selected)));
    this.renderCustomize();
  }

  // ----- selection -----------------------------------------------------------

  /** M102 (owner): LOCKED rides are browsable now — selecting one drives its
   *  ghost onto the pedestal with real stats showing, and the confirm button
   *  becomes a disabled LOCKED · N SP readout instead. Only confirming (and
   *  shopping/customizing) stays gated on actually owning the ride. */
  private select(id: string): void {
    if (id === this.selected) return;
    this.selected = id;
    this.stage?.setHero(id);
    this.refreshHero();
    this.layoutStage();
  }

  private cycle(dir: number): void {
    const ids = VEHICLE_ORDER.map((v) => v.id);
    const cur = Math.max(0, ids.indexOf(this.selected));
    this.select(ids[(cur + dir + ids.length) % ids.length]);
  }

  // ----- rendering -----------------------------------------------------------

  private refreshHero(): void {
    const id = this.selected;
    const promo = !!(this.opts?.promo && this.opts.lock);
    // M139: in the promo showroom every ride reads as on display (lit name,
    // real stats) — the sell is "all of these come with the game".
    const unlocked = promo || this.unlockedIds.includes(id);
    // Locked rides read greyed-out everywhere (owner rule): steel accent on
    // the name/stats, padlock in the name row, the confirm shows the price.
    const accent = unlocked ? (ACCENTS[id] ?? '#ff4438') : '#8fa2b5';
    this.vehNameEl.replaceChildren();
    if (!unlocked) {
      const lock = el('span', 'garage-veh-lock', this.vehNameEl);
      lock.innerHTML = PADLOCK; // static SVG constant
    }
    this.vehNameEl.append(vehicleLabel(id).toUpperCase());
    this.vehNameEl.style.color = accent;
    this.vehNameEl.style.textShadow = unlocked
      ? `0 0 18px ${accent}66, 0 2px 10px rgba(0,0,0,.6)`
      : '0 2px 10px rgba(0,0,0,.6)';
    // The gold-outline CLASS chip + (browsing locked) the gold LOCKED price chip.
    this.typeChipEl.textContent = VEHICLE_TYPES[id] ?? vehicleLabel(id).toUpperCase();
    this.lockChipEl.style.display = unlocked ? 'none' : '';
    if (!unlocked) this.lockChipEl.textContent = `LOCKED · ${unlockThresholdFor(id)} XP`;
    this.statsPanel.style.setProperty('--veh-accent', accent);
    this.statsPanel.classList.toggle('locked', !unlocked);
    const v = tuning.vehicles[id];
    STAT_BARS.forEach((s, i) => {
      const fill = v ? Math.min(1, Math.max(0.06, (v[s.key] - s.min) / (s.max - s.min))) : 0;
      const row = this.statsRows[i];
      if (row) row.fill.style.width = `${Math.round(fill * 100)}%`;
    });
    const [b1, b2] = BLURBS[id] ?? ['', ''];
    if (unlocked) {
      this.blurbEl.textContent = `${b1} ${b2}`;
    } else {
      // Note FIRST — the blurb clamps to one line in the bar, and the gold
      // unlock note is the part that must always survive the ellipsis.
      this.blurbEl.replaceChildren();
      el('span', 'garage-unlock-note', this.blurbEl).textContent =
        `UNLOCKS AT ${unlockThresholdFor(id)} XP`;
      this.blurbEl.append(` ${b1} ${b2}`);
    }
    const ids = VEHICLE_ORDER.map((veh) => veh.id);
    [...this.dotsEl.children].forEach((d, i) => d.classList.toggle('on', ids[i] === id));
    // Chevrons cycle the FULL lineup (locked rides browse too) — always live.
    this.prevBtn.disabled = false;
    this.nextBtn.disabled = false;
    // The confirm only ever fires on an owned ride; locked it becomes the
    // (disabled) price tag. Shop/customize target a ride you own — hidden.
    const gameLock = promo || this.opts?.lock?.confirmBuys !== false ? this.opts?.lock : undefined;
    this.confirmBtn.disabled = !unlocked && !gameLock;
    this.confirmBtn.classList.toggle('locked', !unlocked && !gameLock);
    this.confirmBtn.classList.toggle('garage-go-buy', !!gameLock);
    if (gameLock) {
      // The tour is not owned: the one action here is buying the game.
      this.confirmLabelEl.innerHTML = `<span class="garage-go-lock">${LOCK_SVG}</span>`;
      this.confirmLabelEl.append(`${BUY_GAME_LABEL} · ${gameLock.price}`);
    } else {
      this.confirmLabelEl.textContent = unlocked
        ? (this.opts?.confirmLabel ?? '')
        : `LOCKED · ${unlockThresholdFor(id)} XP`;
    }
    this.shopBtn.style.display = this.opts?.onShop && unlocked ? '' : 'none';
    this.custBtn.style.display =
      this.opts?.onShop && this.opts?.onSaveChange && unlocked ? '' : 'none';
  }

  private buildDots(): void {
    this.dotsEl.replaceChildren();
    VEHICLE_ORDER.forEach((v) => {
      const d = el('span', 'garage-dot', this.dotsEl);
      d.classList.toggle('locked', !this.unlockedIds.includes(v.id));
      d.addEventListener('click', () => {
        this.select(v.id);
        this.restartPromo();
      });
    });
  }

  private refreshPoints(): void {
    if (!this.save) return;
    const xp = this.save.lifetimePoints;
    this.pointsValueEl.textContent = `${xp.toLocaleString('en-US')} XP`;
    this.spValueEl.textContent = `${spBalance(this.save).toLocaleString('en-US')} SP`;
    const up = nextUnlock(this.save);
    // The short note the owner asked for: how many XP until the next ride.
    this.pointsNextEl.textContent = up
      ? `${up.pointsToGo.toLocaleString('en-US')} XP TO ${vehicleLabel(up.id).toUpperCase()}`
      : 'ALL RIDES UNLOCKED';
    // The bar runs from the last unlock threshold you passed to the next one.
    let frac = 1;
    if (up) {
      const next = xp + up.pointsToGo;
      const prev = VEHICLE_ORDER.map((v) => unlockThresholdFor(v.id)).reduce(
        (best, t) => (t <= xp && t > best ? t : best),
        0,
      );
      frac = next > prev ? Math.min(1, Math.max(0, (xp - prev) / (next - prev))) : 0;
    }
    this.pointsBarEl.style.width = `${(frac * 100).toFixed(1)}%`;
  }

  /** M113 placement: the hero on the PAINTED turntable, the rest parked in
   *  the PAINTED bays (portrait crops the bays — a small cascade band takes
   *  over). Customize keeps its proven hero-aside split. */
  private layoutStage(): void {
    if (!this.stage) return;
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (w <= 0 || h <= 0) return;
    const portrait = h > w;
    const short = !portrait && h < 520;

    const targets: Record<string, BayTarget> = {};
    // M115 CUSTOMIZE: the overlay now wears the SHOP's composition, so the car
    // is framed with the shop's own numbers — big in the clear zone left of
    // the docked panel (at the derived faux-centre on desktop), or big above
    // the bottom sheet in portrait. Elsewhere M114 stands: the hero fills the
    // stage between the chevrons via a real fov zoom, so every screen-space
    // anchor stays glued to the painted turntable/bays while the whole lineup
    // renders larger.
    const c = this.customizing ? (portrait ? CUST_PORTRAIT : short ? CUST_SHORT : CUST_LAND) : null;
    this.stage.setFovZoom(c ? c.zoom : portrait ? 1 : short ? 1.18 : 1.26);
    // The fov zoom lifts the whole lineup a notch; the hero-only scale does
    // the rest of the owner's "fill the space between the chevrons" without
    // ballooning the parked bays. Customize's own zoom already sizes the car.
    this.stage.setHeroScale(c ? 1 : portrait ? 1.2 : 1.55);
    // Off the painted turntable while customizing — a soft grounding ring
    // stands in for it (the shop's rule) — and the parked lineup steps out of
    // the shot (a narrow fov would otherwise pile it up behind the hero).
    this.stage.setRingScale(c ? CUST_RING_SCALE : HERO_RING_SCALE);
    this.stage.setSolo(c ? this.selected : null);
    if (c) {
      if (portrait) {
        // M132: stand the car on the strip ABOVE the live detail dock (the
        // shop's own rule — dockLayout.ts), never under it.
        const dockTop = this.custDock.getBoundingClientRect().top;
        const a = portraitCarAnchor(h, dockTop > 0 ? dockTop : null);
        this.stage.setFovZoom(a.zoom);
        targets[this.selected] = { xFrac: a.x, yFrac: a.y };
      } else {
        targets[this.selected] = { xFrac: short ? c.x : fauxCentreX(w), yFrac: c.y };
      }
    } else if (portrait) {
      targets[this.selected] = { xFrac: HERO_ANCHOR_PORTRAIT.x, yFrac: HERO_ANCHOR_PORTRAIT.y };
    } else if (short) {
      targets[this.selected] = { xFrac: HERO_ANCHOR_SHORT.x, yFrac: HERO_ANCHOR_SHORT.y };
    } else {
      // M114: a hair nearer than the painted centre — with the hero lift this
      // fills the chevron gap while the wheels stay on the turntable art.
      const t = coverFrac(TURNTABLE_IMG.x, TURNTABLE_IMG.y, w, h);
      targets[this.selected] = { xFrac: t.x, yFrac: Math.min(t.y + 0.03, HERO_Y_MAX) };
    }
    const rest = VEHICLE_ORDER.map((v) => v.id).filter((id) => id !== this.selected);
    const m = Math.max(1, rest.length - 1);
    rest.forEach((id, i) => {
      if (portrait) {
        const t = i / m;
        targets[id] = {
          xFrac: BAY_PORTRAIT.x0 + (BAY_PORTRAIT.x1 - BAY_PORTRAIT.x0) * t,
          yFrac: BAY_PORTRAIT.y + (i % 2 === 0 ? 0.012 : -0.012),
        };
      } else if (short) {
        targets[id] = { xFrac: BAY_XS_SHORT[i] ?? 0.8, yFrac: BAY_Y_SHORT };
      } else {
        const b = coverFrac(BAY_XS[i] ?? 0.5, BAY_IMG_Y, w, h);
        targets[id] = { xFrac: b.x, yFrac: b.y };
      }
    });
    this.stage.applyTargets(targets);
  }
}
